import { useSession } from "@src/contexts/SessionContext";
import React from "react";
import styled from "styled-components/macro";
import { z } from "zod";
import { cleanVendorName } from "@src/sudozi-schemas/clean-vendor-name";

const logoImageUrlCache: Map<string, string> = new Map();

const Logo = styled.div<{
  dimensionMultiple: number;
  size: number;
  logoUrl: string | undefined;
  companyName: string;
  bgColor: string;
}>`
  width: ${({ dimensionMultiple }) => dimensionMultiple}rem;
  height: ${({ dimensionMultiple }) => dimensionMultiple}rem;
  border-radius: 50%;
  box-shadow: var(--drop-shadow-light);
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ companyName }) =>
    fontColor(companyName, nameToColor(companyName))};
  font-size: ${({ size }) => size * 0.025}rem;
  background: ${({ logoUrl: logo }) => (logo ? `url(${logo})` : "auto")};
  background-color: ${({ bgColor, logoUrl: logo }) =>
    logo ? "auto" : bgColor};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

function companyInitials(companyName: string) {
  let initials = "";
  try {
    initials = companyName
      .replace(/[^a-zA-Z0-9]/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0].toUpperCase())
      .slice(0, 4)
      .join("");
  } finally {
    return initials;
  }
}

export function nameToColor(companyName: string) {
  let hash = 0;
  const salt = 5899; // Adding a salt value to handle single-letter names
  for (let i = 0; i < companyName.length; i++) {
    hash = companyName.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  hash = Math.abs(hash + salt);
  const color = `hsl(${hash % 360}, 80%, 60%)`; // Adjusted saturation and lightness for more distinct colors
  return color;
}

function fontColor(str: string, backgroundColor: string) {
  const r = parseInt(backgroundColor.substr(1, 2), 16);
  const g = parseInt(backgroundColor.substr(3, 2), 16);
  const b = parseInt(backgroundColor.substr(5, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000000" : "#ffffff";
}

const ClearbitResponseSchema = z.array(
  z.object({
    name: z.string(),
    logo: z.string(),
  }),
);

export const CompanyLogo: React.FC<{ name: string; size?: number }> = ({
  name: incomingName,
  size = 28,
}) => {
  const [logoUrl, setLogoUrl] = React.useState<string | undefined>(undefined);
  const dimensionMultiple = size / 16;
  const { enableAutoCompanyLogos } = useSession();

  const name = cleanVendorName(incomingName);

  React.useEffect(() => {
    const controller = new AbortController();

    const fetchLogo = async () => {
      if (!enableAutoCompanyLogos) return;

      if (logoImageUrlCache.has(name)) {
        setLogoUrl(logoImageUrlCache.get(name));
        return;
      }

      const response = await fetch(
        `https://autocomplete.clearbit.com/v1/companies/suggest?query=${name}&size=${size}}`,
      );
      const json = ClearbitResponseSchema.parse(await response.json());

      if (response.status === 200 && json.length > 0) {
        const exactMatch = json.find((item) => item.name === name);
        if (exactMatch) {
          logoImageUrlCache.set(name, exactMatch.logo);
          if (controller.signal.aborted) return;
          setLogoUrl(exactMatch.logo);
          return;
        }
        logoImageUrlCache.set(name, json[0].logo);
        if (controller.signal.aborted) return;
        setLogoUrl(json[0].logo);
      } else {
        logoImageUrlCache.set(name, "");
      }
    };

    try {
      void fetchLogo();
    } catch (e) {}

    return () => {
      controller.abort();
    };
  }, [name, size, enableAutoCompanyLogos]);

  return (
    <Logo
      size={size}
      dimensionMultiple={dimensionMultiple}
      logoUrl={logoImageUrlCache.get(name) || logoUrl}
      bgColor={nameToColor(name)}
      companyName={name}
    >
      {logoUrl || logoImageUrlCache.get(name) ? null : companyInitials(name)}
    </Logo>
  );
};
